import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeftIcon as ArrowLeft, DownloadSimpleIcon as DownloadSimple, ArrowSquareOutIcon as ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * In-app PDF viewer with an always-visible back button.
 *
 * Why PDF.js instead of <iframe src={pdfUrl}>?
 *
 *   iOS Safari (especially inside an installed PWA running in
 *   `display-mode: standalone`) refuses to render PDFs inside an
 *   iframe — instead it tries to hand the file to iOS Quick Look,
 *   which lives OUTSIDE the PWA shell and traps the user with no
 *   way back. PDF.js renders each page to an in-document <canvas>
 *   so iOS never sees a "PDF file" at all — it just sees pixels.
 *
 * Implementation notes:
 *
 *   - pdfjs-dist's legacy build is dynamically imported so the
 *     ~700 KB library only loads when this route is visited.
 *   - The worker is shipped from `/pdfjs/pdf.worker.min.js`
 *     (copied to public/) rather than a CDN so we don't add a
 *     third-party CSP dependency.
 *   - Each page renders at a `devicePixelRatio`-aware scale so it
 *     looks sharp on Retina screens. We cap width to the iframe
 *     container width to avoid horizontal scrolling on phones.
 *   - For multi-page PDFs we render them in sequence so the user
 *     can scroll the whole document inside our shell.
 */
export default function PdfViewer() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const src = params.get("src") || "";
  const title = params.get("title") || "Document";
  const downloadName = params.get("dl") || "";

  const [status, setStatus] = useState(src ? "loading" : "empty");
  const [errorMsg, setErrorMsg] = useState("");

  const onBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard");
  };

  // Lazy-load pdfjs-dist + render every page into the container.
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    let loadingTask = null;

    (async () => {
      try {
        // Legacy build = CRA-friendly (no `import.meta.url` reliance).
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
        pdfjs.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js";

        loadingTask = pdfjs.getDocument({
          url: src,
          // Allow our auth-bearing query string to pass through.
          withCredentials: false,
        });
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const container = containerRef.current;
        if (!container) return;
        // Clear any previous render (e.g. if `src` changed).
        container.innerHTML = "";

        // Page width budget — clamp to 920px so a 27" monitor doesn't
        // render the cert at zoo-mural size. Account for the 16px
        // horizontal padding on either side of the container.
        const dpr = window.devicePixelRatio || 1;
        const cssWidth = Math.min(container.clientWidth - 32, 920);

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1 });
          const scale = cssWidth / viewport.width;
          const scaled = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.style.cssText =
            "display:block;margin:14px auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);border-radius:6px;";
          canvas.style.width = `${scaled.width}px`;
          canvas.style.height = `${scaled.height}px`;
          canvas.width = Math.floor(scaled.width * dpr);
          canvas.height = Math.floor(scaled.height * dpr);
          const ctx = canvas.getContext("2d");
          ctx.scale(dpr, dpr);
          container.appendChild(canvas);

          await page.render({
            canvasContext: ctx,
            viewport: scaled,
          }).promise;
        }

        if (!cancelled) setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        // Treat AbortError quietly — happens during route changes.
        if (e && e.name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.warn("PDF render failed", e);
        setStatus("error");
        setErrorMsg((e && e.message) || "Could not render the document.");
      }
    })();

    return () => {
      cancelled = true;
      try { loadingTask && loadingTask.destroy && loadingTask.destroy(); } catch (_e) { /* ignore */ }
    };
  }, [src]);

  return (
    <div
      className="min-h-screen bg-[#0E0E0E] flex flex-col"
      data-testid="pdf-viewer-page"
    >
      {/* Sticky chrome — `sticky top-0` rather than `fixed` so the
          canvas list below can be sized as a normal flex child. */}
      <header
        className="sticky top-0 z-10 bg-[#0E0E0E]/95 backdrop-blur-md border-b border-white/10 px-3 py-2 flex items-center gap-2"
        data-testid="pdf-viewer-header"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="rounded-full h-9 px-3 text-white hover:bg-white/10 hover:text-white"
          data-testid="pdf-viewer-back-btn"
          aria-label="Back to the registry"
        >
          <ArrowLeft size={18} weight="bold" className="mr-1.5" />
          <span className="hidden sm:inline">Back</span>
          <span className="sm:hidden">Done</span>
        </Button>
        <p className="flex-1 text-white text-sm font-semibold truncate px-1">
          {title}
        </p>
        {src && (
          <>
            <a
              href={src}
              download={downloadName || undefined}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white px-2.5 py-1.5 rounded-full hover:bg-white/10"
              data-testid="pdf-viewer-download-btn"
            >
              <DownloadSimple size={14} weight="bold" />
              Download
            </a>
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white px-2.5 py-1.5 rounded-full hover:bg-white/10"
              data-testid="pdf-viewer-newtab-btn"
            >
              <ArrowSquareOut size={14} weight="bold" />
              Open in tab
            </a>
            <a
              href={src}
              download={downloadName || undefined}
              className="sm:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-white/80 hover:text-white hover:bg-white/10"
              data-testid="pdf-viewer-mobile-download-btn"
              aria-label="Download PDF"
            >
              <DownloadSimple size={18} weight="bold" />
            </a>
          </>
        )}
      </header>

      <div className="flex-1 bg-[#1F1F23]">
        {status === "empty" && (
          <div
            className="h-[60vh] flex items-center justify-center text-white/70"
            data-testid="pdf-viewer-empty"
          >
            <div className="text-center">
              <p className="text-base mb-3">No document to display.</p>
              <Link
                to="/dashboard"
                className="text-gold underline decoration-gold underline-offset-2"
              >
                Return to the registry
              </Link>
            </div>
          </div>
        )}
        {status === "loading" && (
          <div
            className="h-[60vh] flex items-center justify-center text-white/70"
            data-testid="pdf-viewer-loading"
          >
            <div className="text-center">
              <div className="inline-block h-7 w-7 border-2 border-white/30 border-t-white rounded-full animate-spin mb-3" />
              <p className="text-sm">Preparing your document…</p>
            </div>
          </div>
        )}
        {status === "error" && (
          <div
            className="h-[60vh] flex items-center justify-center text-white/80 px-6"
            data-testid="pdf-viewer-error"
          >
            <div className="text-center max-w-md">
              <p className="font-semibold mb-2">We couldn&apos;t open this document inline.</p>
              <p className="text-sm text-white/60 mb-4">{errorMsg}</p>
              <a
                href={src}
                download={downloadName || undefined}
                className="inline-flex items-center gap-1.5 bg-white text-ink rounded-full px-4 py-2 text-sm font-semibold"
                data-testid="pdf-viewer-fallback-download"
              >
                <DownloadSimple size={14} weight="bold" />
                Download instead
              </a>
            </div>
          </div>
        )}
        <div
          ref={containerRef}
          className={status === "ready" ? "px-4 py-3" : "hidden"}
          data-testid="pdf-viewer-canvas-container"
        />
      </div>
    </div>
  );
}
